import { CAMERA_TIMING_POLICY, cameraGuardMs } from "./camera_timing_policy"

// Token-guarded camera commands with exact-target corrections — the web port of
// the app's tested controller (react-native/src/map/deferredFlyTo.js, minus the
// iOS modal queue/flush, which the web doesn't need). Every scheduled correction
// is tracked and guarded by an intent token so a NEWER camera intent (search,
// find, follow, disconnect) cancels the in-flight one — a late correction would
// otherwise yank the camera off the thing the user just asked for.
//
// Hooks:
//   onCommand()          fired before EVERY camera call (initial + corrections) —
//                        the Stimulus controller marks the move programmatic here
//   options.onRun(info)  fired when a move actually runs, with the real
//                        { guardMs } window (duration + last correction)
export function createCameraIntent(getMap, { onCommand } = {}) {
  let token = 0
  let timers = []

  const clearTimers = () => {
    timers.forEach(clearTimeout)
    timers = []
  }

  const later = (fn, ms, expectedToken) => {
    const id = setTimeout(() => {
      timers = timers.filter(timerId => timerId !== id)
      if (expectedToken !== token) return
      fn()
    }, ms)
    timers.push(id)
  }

  const cancel = () => {
    token += 1
    clearTimers()
  }

  const move = (target, options = {}) => {
    const map = getMap()
    if (!map || !target) return
    token += 1
    clearTimers()
    const expectedToken = token

    const method = options.method === "jumpTo" ? "jumpTo" : options.method === "flyTo" ? "flyTo" : "easeTo"
    const duration = options.duration ?? (method === "jumpTo" ? 0 : method === "flyTo" ? CAMERA_TIMING_POLICY.flyDurationMs : CAMERA_TIMING_POLICY.defaultEaseDurationMs)
    const correctionDuration = options.correctionDuration ?? CAMERA_TIMING_POLICY.correctionDurationMs
    const correctionDelays = options.correctionDelays ?? []
    options.onRun?.({ target, method, duration, guardMs: cameraGuardMs(duration, correctionDelays, correctionDuration) })

    onCommand?.()
    if (method === "jumpTo") map.jumpTo(target)
    else map[method]?.({ ...target, duration })
    // Exact-target corrections in case another animation nipped the move.
    correctionDelays.forEach(delay => {
      later(() => {
        const m = getMap()
        if (!m) return
        onCommand?.()
        m.easeTo({ ...target, duration: correctionDuration })
      }, delay, expectedToken)
    })
  }

  return { move, cancel }
}
